<template>
    <div class="relative inline-block text-left">
        <button
            @click="open = !open"
            type="button"
            class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
            <svg viewBox="0 0 20 20" fill="currentColor" class="pencil w-4 h-4">
                <path
                    d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"
                ></path>
            </svg>
            <SlideOver size="xxl" :show="open" @close="open = false">
                <form
                    :action="question.links.update"
                    method="post"
                    @submit.prevent="submit"
                    class="flex flex-col h-full"
                    id="question_edit_form"
                >
                    <div class="flex-1 md:grid md:grid-cols-12 md:gap-6 overflow-y-scroll">
                        <div class="sm:col-span-12">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.title')" for="title" />
                                <FormInput
                                    required
                                    id="title"
                                    v-model="form.title"
                                    name="title"
                                    type="text"
                                    max="191"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.title" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-12">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.description')" for="description" />
                                <div v-auto-animate>
                                    <FormTextarea
                                        id="description"
                                        v-model="form.description"
                                        name="description"
                                        type="text"
                                        class="mt-1"
                                        v-if="form.is_latex"
                                    />
                                    <Editor
                                        id="description"
                                        v-model="form.description"
                                        name="description"
                                        class="mt-1"
                                        :only="['bold', 'italic']"
                                        v-else
                                    />
                                </div>
                                <FormError :value="form.errors.description" class="mt-2" />
                            </div>
                            <div class="mt-1">
                                <label class="mr-1">
                                    <input
                                        :value="false"
                                        name="is_latex"
                                        v-model="form.is_latex"
                                        type="radio"
                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                                    />
                                    {{ $t('messages.formatted_text') }}
                                </label>
                                <label class="mr-1">
                                    <input
                                        :value="true"
                                        name="is_latex"
                                        v-model="form.is_latex"
                                        type="radio"
                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                                    />
                                    <span>
                                        {{ $t('messages.is_latex') }}

                                        <span class="inline-block">
                                            <Tippy>
                                                <IconHelpCircle
                                                    class="h-6 w-6 text-gray-400 group-hover:text-gray-300 group-focus:text-gray-300 transition ease-in-out duration-150"
                                                />
                                                <template #content>
                                                    <span>{{ $t('messages.is_latex_explanation') }}</span>
                                                </template>
                                            </Tippy>
                                        </span>
                                    </span>
                                </label>
                            </div>
                            <div class="mt-1" v-auto-animate>
                                <AppWell v-if="form.is_latex">
                                    <LatexDisplay v-model="form.description" />
                                </AppWell>
                            </div>
                        </div>

                        <div class="sm:col-span-12">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.hint')" for="hint">
                                    <template v-slot:corner>
                                        <span class="text-gray-500 text-sm"
                                            >({{ $t('messages.for_adaptations') }})</span
                                        >
                                    </template>
                                </FormLabel>
                                <FormInput
                                    id="hint"
                                    v-model="form.hint"
                                    name="hint"
                                    type="text"
                                    max="191"
                                    :placeholder="$t('messages.(optional)')"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.hint" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-12" v-if="form.image_path">
                            <div class="mt-1">
                                <img :src="question.image_path" alt="cover" class="h-auto" />
                            </div>
                            <div class="mt-1">
                                <AppButton @click="form.image_path = null" element="button" type="button">
                                    {{ $tc('messages.delete_photo', 2) }}
                                </AppButton>
                            </div>
                        </div>

                        <div class="sm:col-span-12">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.image_bank')" for="shared_image_name" />
                                <FormInput
                                    id="shared_image_name"
                                    v-model="form.shared_image_name"
                                    name="shared_image_name"
                                    list="shared_image_name_suggestions"
                                    type="text"
                                    max="191"
                                    :placeholder="$t('messages.(optional)')"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.shared_image_name" class="mt-2" />
                                <QuestionSharedImageSuggestions :search="form.shared_image_name" />
                            </div>
                        </div>

                        <div class="sm:col-span-12 mt-3 p-2 bg-gray-100 rounded-lg" v-if="question.shared_image">
                            <img :src="question.shared_image" alt="cover" class="h-auto" />
                        </div>

                        <div class="sm:col-span-12" v-else>
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.image')" for="image" />
                                <input
                                    id="image"
                                    @input="form.image = $event.target.files[0]"
                                    name="image"
                                    type="file"
                                    accept="image/png,image/jpg,image/jpeg,"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.image" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-6" v-if="canHaveLatexAnswers">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.is_latex_answers')" for="is_latex_answers" />
                                <input
                                    id="is_latex_answers"
                                    name="is_latex_answers"
                                    v-model="form.is_latex_answers"
                                    type="checkbox"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                                <FormError :value="form.errors.is_latex_answers" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-12">
                            <QuizSpecificOptionsForm :type="form.type" v-model="form.options" :errors="form.errors" />
                        </div>

                        <div class="sm:col-span-6">
                            <FormLabel :value="$t('messages.weight')" for="weight">(1-5)</FormLabel>
                            <FormInput
                                required
                                id="weight"
                                v-model.number="form.weight"
                                name="weight"
                                type="number"
                                step="1"
                                max="5"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.weight" class="mt-2" />
                        </div>

                        <div class="sm:col-span-6">
                            <FormLabel :value="$t('messages.difficulty')" for="difficulty" />
                            <FormSmallNumberRangeInput v-model.number="form.difficulty" name="difficulty" :max="5" />
                            <FormError :value="form.errors.difficulty" class="mt-2" />
                        </div>

                        <div class="sm:col-span-6">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.share_on_question_bank')" for="is_negative" />
                                <input
                                    name="selected"
                                    v-model="form.is_reusable"
                                    type="checkbox"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                                <FormError :value="form.errors.is_reusable" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-6">
                            <div class="mt-1">
                                <FormLabel :value="$tc('messages.languages', 1)" for="locale" />
                                <FormSelect required v-model="form.locale" name="locale" :options="languages" />
                                <FormError :value="form.errors.locale" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-6">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.is_calculator_allowed')" for="is_calculator_allowed" />
                                <FormSelect
                                    v-model="form.is_calculator_allowed"
                                    name="locale"
                                    :options="calculatorOptions"
                                />
                                <FormError :value="form.errors.is_calculator_allowed" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-12">
                            <FormLabel :value="$tc('messages.tags', 2)" for="is_negative" />
                            <TagsInput v-model="form.tags"></TagsInput>
                            <FormError :value="form.errors.tags" class="mt-2" />
                        </div>
                    </div>
                </form>
                <template v-slot:bottom>
                    <div class="w-full">
                        <div class="mt-5" v-if="isReview">
                            <div>
                                <div>
                                    <FormLabel :value="$t('messages.question_review_reply')" for="reply" />
                                </div>
                                <FormTextarea id="reply" v-model="form.reply" name="reply" type="text" class="mt-1" />
                            </div>

                            <div class="mt-1">
                                <FormLabel
                                    :value="$t('messages.should_reevaluate_attempts')"
                                    for="should_reevaluate_attempts"
                                />
                                <input
                                    id="should_reevaluate_attempts"
                                    name="should_reevaluate_attempts"
                                    v-model="form.should_reevaluate_attempts"
                                    type="checkbox"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                                <FormError :value="form.errors.should_reevaluate_attempts" class="mt-2" />
                            </div>
                        </div>
                        <div class="flex justify-end mt-5">
                            <SubmitFormButton
                                :button-text="$t('messages.save')"
                                :processing="form.processing"
                                form="question_edit_form"
                            />
                        </div>
                    </div>
                </template>
            </SlideOver>
        </button>
    </div>
</template>

<script>
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import AppButton from '@/components/AppButton.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import TagsInput from '@/components/Quiz/TagsInput.vue';
import FormSmallNumberRangeInput from '@/components/Forms/FormSmallNumberRangeInput.vue';
import QuizSpecificOptionsForm from '@/components/Quiz/QuizSpecificOptionsForm.vue';
import SlideOver from '@/components/SlideOver.vue';
import LatexDisplay from '@/components/LatexDisplay.vue';
import AppWell from '@/Shared/AppWell.vue';
import { IconHelpCircle } from '@tabler/icons-vue';
import { Tippy } from 'vue-tippy';
import Editor from '@/components/Forms/RichTextEditor/Editor.vue';
import QuestionSharedImageSuggestions from '@/components/Quiz/QuestionSharedImageSuggestions.vue';
import SubmitFormButton from '../../Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    emits: ['close'],
    name: 'QuestionEditFormPanel',

    components: {
        SubmitFormButton,
        QuestionSharedImageSuggestions,
        Editor,
        Tippy,
        IconHelpCircle,
        AppWell,
        LatexDisplay,
        SlideOver,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        FormTextarea,
        AppButton,
        TagsInput,
        FormSmallNumberRangeInput,
        QuizSpecificOptionsForm,
    },

    props: {
        defaults: {
            required: false,
            type: Object,
        },
        question: {
            required: false,
            type: Object,
        },
        isReview: {
            required: false,
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            open: false,
            languages: ['ca', 'es', 'en'].map((l) => {
                return {
                    value: l,
                    text: this.$t(`locales.${l}`),
                };
            }),
            calculatorOptions: [
                {
                    value: 0,
                    text: this.$t(`messages.calculator_is_not_allowed`),
                },
                {
                    value: 1,
                    text: this.$t(`messages.calculator_is_allowed`),
                },
            ],
            form: this.$inertia.form({
                _method: 'patch',
                type: this.question.type,
                title: this.question.title,
                hint: this.question.hint,
                correct_answer: this.question.correct_answer,
                description: this.question.description,
                subject_id: this.question.subject_id,
                locale: this.question.locale,
                difficulty: this.question.difficulty,
                weight: this.question.weight,
                is_reusable: this.question.is_reusable,
                is_latex: this.question.is_latex,
                is_latex_answers: this.question.is_latex_answers,
                grade_level_ids: this.question.grade_level_ids,
                tags: this.question.tags,
                image: null,
                image_path: this.question.image_path,
                shared_image_name: this.question.shared_image_name,
                is_calculator_allowed: this.question.is_calculator_allowed,
                options: this.question.options,
                should_reevaluate_attempts: false,
                reply: null,
            }),
        };
    },

    methods: {
        dismiss() {
            this.$emit('close');
        },
        submit() {
            this.form.post(this.question.links.update, {
                preserveScroll: true,
                onSuccess: () => {
                    this.open = false;
                    this.form.reset('title', 'correct_answer_index', 'description', 'options', 'difficulty', 'hint');
                },
            });
        },
    },
    computed: {
        canHaveLatexAnswers() {
            return ['multiple-choice', 'math-multiple-magnitudes', 'math-xyz-answers'].includes(this.question.type);
        },
    },
};
</script>
